import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Menu, MenuItem } from '@mui/material';
import { Add } from '@mui/icons-material';
import _ from 'lodash';

import {
  ISitePermission,
  UserRole,
  UsersAndPermissionDefaultSort,
  UsersAndPermissionId,
  UsersAndPermissionTableColumn,
  UsersAndPermissionTableColumnTitles,
  userStatuses,
  userTitles,
  userTypes
} from 'constants/user.constants';
import { IColumnType } from 'components/Table/types';

import SelectBox from 'components/SelectBox';
import BasicTable from 'components/Table';

import { pagesEnum } from 'constants/ui.constants';
import { useAppSelector } from 'hooks/redux.hooks';
import CircularIndeterminate from 'components/Loading';
import { menuOptions } from './helper';
import { Site } from 'constants/sites.constants';
import { filterByFields } from '../../utils/global.util';
import { DataFetchKeys, useDataFetchContext } from '../../context/DataFetchContext';
import { PageContentAddNew, PageContentContainer, PageContentFilter, PageContentTable } from '../PageContentContainer';
import { useDebounce } from 'hooks/useDebounce';

const calcParsedSitesOptions = (sites: Site[] | undefined): string[] => {
  return (sites || []).map((site) => site.name || '');
};

const UsersAndPermissionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { type: userType, siteId } = useParams();
  const { users, loading } = useAppSelector((state) => state.user);
  const { sites } = useAppSelector((state) => state.sites);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>(createFilters());
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedValue = useDebounce(searchValue);
  const { loadDataByKeys } = useDataFetchContext();

  function createFilters(
    userTypeValues: string[] = [],
    userTypeOptions: string[] = [],
    sitesValues: string[] = [],
    sitesOptions: string[] = [],
    statusValue = ''
  ) {
    return {
      userType: {
        displayName: 'User Type',
        value: userTypeValues?.length > 0 ? userTypeValues : ['All'],
        options: userTypeOptions,
        multiple: true
      },
      sites: {
        displayName: 'Sites',
        value: sitesValues?.length > 0 ? sitesValues : ['All'],
        options: sitesOptions,
        multiple: true
      },
      status: {
        displayName: 'Status',
        value: statusValue || 'All',
        options: userStatuses
      }
    };
  }

  const open = Boolean(anchorEl);

  useEffect(() => {
    const initialFilters = createFilters(
      searchParams.getAll('userType'),
      userTypes,
      searchParams.getAll('sites'),
      calcParsedSitesOptions(sites),
      searchParams.get('status') || undefined
    );

    setFilterValues(initialFilters);
  }, [userTypes, sites, searchParams]);

  useEffect(() => {
    if (!users?.length) {
      loadDataByKeys([DataFetchKeys.USERS]);
    }
    if (!sites?.length) {
      loadDataByKeys([DataFetchKeys.SITES]);
    }
  }, [users?.length, sites?.length]);

  useEffect(() => {
    if (filterValues) {
      if (userType) {
        if (filterValues.userType) {
          if (filterValues.userType.value !== userTitles[userType as keyof typeof userTitles])
            onFilterChange('userType', userTitles[userType as keyof typeof userTitles]);
        }
      }
      if (siteId)
        if (filterValues.sites) {
          const site = sites?.find((site: Site) => {
            return site.id !== undefined && site.id === Number(siteId);
          });
          if (site?.name && filterValues.sites.value !== site.name) {
            onFilterChange('sites', site.name);
          }
        }
    }
  }, [userType, siteId, filterValues, users]);

  const onFilterChange = (filterType: string, filterValue: string | string[]) => {
    const updatedFilterValues = {
      ...filterValues,
      [filterType]: { ...filterValues[filterType as keyof typeof filterValues], value: filterValue }
    };
    setFilterValues(updatedFilterValues);

    // Update search params
    const newSearchParams = new URLSearchParams(searchParams);
    if (Array.isArray(filterValue)) {
      newSearchParams.delete(filterType);
      filterValue.forEach((value) => newSearchParams.append(filterType, value));
    } else {
      newSearchParams.set(filterType, filterValue);
    }
    setSearchParams(newSearchParams);
  };

  const updateTable = useCallback(() => {
    if (!_.keys(filterValues).length) return [];
    return users.filter((user) => {
      return (
        isUserStatusMatches(Boolean(user.active)) &&
        isUserTypesMatches(user.sitePermissions, !!user.isSuperAdmin) &&
        isUserSitesMatches(user.sitePermissions)
      );
    });
  }, [users, filterValues]);

  const isUserStatusMatches = (isActive: boolean) => {
    return (
      (filterValues.status.value === 'Active' && isActive) ||
      (filterValues.status.value === 'Inactive' && !isActive) ||
      !filterValues.status.value ||
      filterValues.status.value === 'All'
    );
  };

  const isUserTypesMatches = (sitePermissions: ISitePermission[], isAdmin: boolean) => {
    return (
      !filterValues.userType.value ||
      filterValues.userType.value.includes('All') ||
      (filterValues.userType.value.includes('Super Admin') && isAdmin) ||
      sitePermissions.some((sitePermission) => {
        const userType = userTitles[sitePermission.role as keyof typeof userTitles];

        return filterValues.userType.value.includes(userType);
      })
    );
  };

  const isUserSitesMatches = (sitePermissions: ISitePermission[]) => {
    return (
      !filterValues.sites.value ||
      filterValues.sites.value.includes('All') ||
      sitePermissions.some((sitePermission) => {
        return filterValues.sites.value.includes(sitePermission.name);
      })
    );
  };

  const usersColumns = useMemo(() => {
    const columns: IColumnType[] = [];
    _.keys(UsersAndPermissionTableColumn).forEach((element) => {
      const props: IColumnType = {
        fieldId: UsersAndPermissionId,
        name: element,
        title:
          UsersAndPermissionTableColumnTitles[element as keyof typeof UsersAndPermissionTableColumnTitles] || element,
        type: UsersAndPermissionTableColumn[element],
        customValueHandle: (val, rec) => {
          if (element === 'role') {
            if (rec?.isSuperAdmin) {
              return userTitles[UserRole.BW_ADMIN];
            }
            if (rec?.role) {
              return userTitles[rec.role as unknown as keyof typeof userTitles];
            } else {
              return 'N/A';
            }
          }
          if (_.isArray(val)) {
            return val.length;
          }
          return rec.active ? 'Active' : 'Inactive';
        }
      };
      columns.push(props);
    });
    return columns;
  }, []);

  const onResetSelections = () => {
    const resetedFilterValues = createFilters([], userTypes, [], calcParsedSitesOptions(sites));
    setFilterValues(resetedFilterValues);

    // Reset search params
    const newSearchParams = new URLSearchParams();
    newSearchParams.append('userType', 'All');
    newSearchParams.append('sites', 'All');
    newSearchParams.append('status', 'All');
    setSearchParams(newSearchParams);

    navigate(`/${pagesEnum.USERS_AND_PERMISSIONS}`, { replace: true });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onAddNewUserClick = (newUserType: string) => {
    handleClose();
    navigate(`/${pagesEnum.USERS_AND_PERMISSIONS}/new/${newUserType}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const filteredUsersAndPermissionsData = useMemo(() => {
    const filtered = updateTable();
    return filterByFields(filtered, debouncedValue, ['firstName', 'lastName'], [['firstName', 'lastName']]);
  }, [updateTable, debouncedValue]);

  const actionsCells = useMemo(
    () => [
      {
        element: (
          <Button
            color='secondary'
            sx={{ marginRight: '46px', textTransform: 'none', fontWeight: 'bold' }}
            variant='text'
          >
            Info
          </Button>
        ),
        onClick: (user) => {
          navigate(`/${pagesEnum.USERS_AND_PERMISSIONS}/${user.id}`);
        }
      }
    ],
    [navigate]
  );

  if (loading) return <CircularIndeterminate />;

  return (
    <PageContentContainer
      pageName='Users and Permission'
      searchValue={searchValue}
      filteredResultsCount={filteredUsersAndPermissionsData?.length || 0}
      resetFilterClicked={onResetSelections}
      searchValueChanged={handleInputChange}
    >
      <PageContentAddNew>
        <Button
          sx={{ textTransform: 'none' }}
          color='secondary'
          variant='contained'
          startIcon={<Add />}
          onClick={handleClick}
        >
          Add New User
        </Button>
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button'
          }}
        >
          <MenuItem
            onClick={() => {
              onAddNewUserClick('new-super-admin');
            }}
          >
            {menuOptions.superAdmin}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onAddNewUserClick('new-BWMR');
            }}
          >
            {menuOptions.bwmr}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onAddNewUserClick('new-HCP');
            }}
          >
            {menuOptions.hcp}
          </MenuItem>
        </Menu>
      </PageContentAddNew>
      <PageContentFilter>
        {_.map(_.keys(filterValues), (filterValuesKey) => {
          return (
            <SelectBox
              key={filterValuesKey}
              items={filterValues[filterValuesKey as keyof typeof filterValues].options}
              values={filterValues[filterValuesKey as keyof typeof filterValues].value}
              label={filterValues[filterValuesKey as keyof typeof filterValues].displayName}
              inputLabel={filterValues[filterValuesKey as keyof typeof filterValues].displayName}
              type={filterValuesKey}
              onFilterChange={onFilterChange}
              withAllOption
              multiple={filterValues![filterValuesKey as keyof typeof filterValues].multiple}
            />
          );
        })}
      </PageContentFilter>
      <PageContentTable>
        <BasicTable
          fieldId='id'
          data={filteredUsersAndPermissionsData}
          columns={usersColumns}
          defaultSortBy={UsersAndPermissionDefaultSort}
          actionsCell={actionsCells}
        />
      </PageContentTable>
    </PageContentContainer>
  );
};

export default UsersAndPermissionPage;
